import { useEffect, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Zap, Sparkles, Heart, Bell } from "lucide-react";
import { toast } from "sonner";

const ICONS = {
  welcome_new_enrollment: Sparkles,
  winback_after_drop: Heart,
  class_reminder_24h: Bell,
};

export default function Automations() {
  const [autos, setAutos] = useState([]);
  const [templates, setTemplates] = useState([]);

  const load = async () => {
    try {
      const [{ data: a }, { data: t }] = await Promise.all([api.get("/automations"), api.get("/templates")]);
      setAutos(a); setTemplates(t);
    } catch (e) { toast.error(formatApiError(e)); }
  };
  useEffect(() => { load(); }, []);

  const update = async (id, patch) => {
    try {
      await api.put(`/automations/${id}`, patch);
      toast.success("Saved");
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const delayLabel = (mins) => {
    if (!mins) return "Immediately";
    if (mins < 60) return `${mins} min after`;
    if (mins < 1440) return `${Math.round(mins / 60)} hr after`;
    return `${Math.round(mins / 1440)} days after`;
  };

  return (
    <div className="h-full overflow-y-auto thin-scroll" data-testid="automations-page">
      <div className="max-w-5xl mx-auto p-8">
        <div className="mb-6">
          <h1 className="text-3xl font-bold tracking-tight text-slate-900 flex items-center gap-2">
            <Zap className="w-7 h-7 text-[#D9232E]" /> Automations
          </h1>
          <p className="text-sm text-slate-500 mt-1">Fire a text automatically when Jackrabbit reports a matching event. Message copy lives in the Templates tab — edit there, applies here.</p>
        </div>

        <div className="space-y-4">
          {autos.map((a) => {
            const Icon = ICONS[a.trigger] || Zap;
            return (
              <div key={a.id} className="bg-white border border-slate-200 rounded-lg p-5" data-testid={`automation-${a.trigger}`}>
                <div className="flex items-start justify-between gap-4 mb-4">
                  <div className="flex items-start gap-3 flex-1">
                    <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${a.enabled ? "bg-red-50" : "bg-slate-100"}`}>
                      <Icon className={`w-5 h-5 ${a.enabled ? "text-[#D9232E]" : "text-slate-400"}`} />
                    </div>
                    <div className="flex-1">
                      <div className="font-bold text-slate-900 text-[15px]">{a.label}</div>
                      <p className="text-xs text-slate-500 mt-0.5">{a.description}</p>
                    </div>
                  </div>
                  <Switch
                    checked={!!a.enabled}
                    onCheckedChange={(v) => update(a.id, { enabled: v })}
                    data-testid={`toggle-${a.trigger}`}
                  />
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-3 pt-4 border-t border-slate-100">
                  <div>
                    <Label className="text-xs">Template</Label>
                    <Select value={a.template_id || ""} onValueChange={(v) => update(a.id, { template_id: v })}>
                      <SelectTrigger data-testid={`template-select-${a.trigger}`}><SelectValue placeholder="Pick a template..." /></SelectTrigger>
                      <SelectContent>
                        {templates.map((t) => <SelectItem key={t.id} value={t.id}>{t.category} · {t.name}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label className="text-xs">Delay (minutes)</Label>
                    <Input
                      type="number"
                      min="0"
                      value={a.delay_minutes || 0}
                      onChange={(e) => update(a.id, { delay_minutes: parseInt(e.target.value || "0", 10) })}
                      data-testid={`delay-input-${a.trigger}`}
                    />
                    <p className="text-[11px] text-slate-500 mt-1">{delayLabel(a.delay_minutes)}. Set to 0-5 for instant, larger values schedule for later.</p>
                  </div>
                </div>

                {a.template && (
                  <div className="mt-3 p-3 bg-slate-50 rounded border border-slate-100 text-xs text-slate-600">
                    <div className="font-semibold text-slate-500 uppercase tracking-wide text-[10px] mb-1">Message preview</div>
                    {a.template.body}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}
